import { useState, useEffect } from 'react';
import { Theme } from '@mui/system';
import { makeStyles } from '@mui/styles';
import { List, ListItemIcon, ListItemButton, ListItemText, useTheme } from '@mui/material';
import { Icon } from '@ts/storybook';
import KPI from '@components/KPI';
import { IAppProps } from '@interfaces/App.interface';
import { KPI_EXECUTIONS_STATISTICS_URL, KPI_LATEST_EXECUTION_STATISTICS_URL, KPI_URL, KPI_OPERATION_TYPES } from './constants';
import KpiResults from './KpiResults';
import { IStatistic, IDataKPICard, convertFromMilliseconds, CKPI, IKpiExecutionStatistics, formatDate } from '@ts/ui-models';
import { useAppDispatch, useClassSelector } from '@redux/store';
import { getListKPIs } from '@services/ApiService';
import { KPIActions } from '@redux/slices/states/kpi/KPISlice';
import { fetchData } from '@redux/slices/functionals/request/requestActions';
import cronstrue from 'cronstrue/i18n';
import { useTranslation } from 'react-i18next';
import { IconButtonPopover, Typography, Grid, GridContainerFullHeight } from '@ts/storybook';
import { CronParamsDto } from '@ts/ui-models';

const useStyles = makeStyles<Theme, { theme: Theme }>(() => {
  return {
    gridLeft: { borderRight: '1px solid lightgray', height: '100%' },
    listSubheader: (props: { theme: Theme }) => ({
      color: props.theme.palette?.primary.main,
      fontSize: '1.4rem',
    }),
    listItemKpi: (props: { theme: Theme }) => ({
      '&:hover': {
        color: props.theme.palette?.primary.main,
      },
    }),
    scrollableList: {
      height: '100%',
      overflowY: 'auto',
    },
  };
});

interface ListCard {
  id: string;
  title: string;
  data: IDataKPICard;
  loading: boolean;
}

interface IKpiMetricsProps extends IAppProps {
  kpiId?: string;
  executionId?: string;
}

const KpiMetrics = (props: IKpiMetricsProps) => {
  const theme = useTheme();
  const className = useStyles({ theme });
  const dispatch = useAppDispatch();
  const listKpiDefinition: any[] = useClassSelector(state => state.KPI.kpiList, CKPI);
  const { i18n } = useTranslation();

  const [timestampExecution, setTimestampExecution] = useState<Date>();
  const [listKPICards, setListKPICards] = useState<ListCard[]>([
    { id: 'minimum_value', title: 'Minimum', data: { value: 0, oldValue: 0 }, loading: true },
    { id: 'maximum_value', title: 'Maximum', data: { value: 0, oldValue: 0 }, loading: true },
    { id: 'arithmetic_mean', title: 'Average', data: { value: 0, oldValue: 0 }, loading: true },
  ]);
  const kpiSelected = useClassSelector(state => state.KPI.kpiSelected, CKPI);

  //#region Handlers
  const handleListItemClick = (kpi: CKPI) => {
    dispatch(KPIActions.updateKPIState({ key: 'kpiSelected', value: kpi }));
  };
  //#endregion

  //#region Functions
  const getValueFormat = v => {
    switch (kpiSelected.kpiOperation.kpiOperationTypeId) {
      case 'count_events':
      case 'value_between_readings':
        return v;

      default:
        return v === 0 ? '0s' : convertFromMilliseconds(v, 'string');
    }
  };

  const responseStatistics = v => {
    const res: IKpiExecutionStatistics = v.payload?.data;
    if (res.timestamp) setTimestampExecution(new Date(res.timestamp));
    if (res?.statistics) {
      const statistics: IStatistic[] = res.statistics;
      setListKPICards(
        listKPICards?.map((item: ListCard) => {
          let result = statistics?.find(stat => stat.kpiStatisticTypeId === item.id);
          if (result?.result) {
            let resultV = getValueFormat(result.result);
            return { ...item, data: { value: resultV, oldValue: 0 }, loading: false };
          }
          return { ...item, data: { value: undefined, oldValue: 0 }, loading: false };
        })
      );
    } else {
      setListKPICards(
        listKPICards?.map((item: ListCard) => {
          return { ...item, data: { value: undefined, oldValue: 0 }, loading: false };
        })
      );
    }
  };

  const getDataStatistics = () => {
    if (kpiSelected?.getID()) {
      dispatch(
        fetchData({
          url: props.executionId
            ? KPI_EXECUTIONS_STATISTICS_URL(kpiSelected?.getID(), props.executionId)
            : KPI_LATEST_EXECUTION_STATISTICS_URL(kpiSelected?.getID()),
          dataBody: {},
        })
      ).then(v => responseStatistics(v));
    }
  };

  const getIconFromKpiDefinition = (kpiDefinition: CKPI) => {
    const isSelected = kpiSelected?.getID() === kpiDefinition.getID();
    const kpiOperationType = KPI_OPERATION_TYPES.find(type => type.id === kpiDefinition.kpiOperation.kpiOperationType.logicalId);
    if (kpiOperationType) {
      if (kpiOperationType?.icon2)
        return (
          <>
            {renderIconList(isSelected, kpiOperationType.icon)}
            {renderIconList(isSelected, kpiOperationType.icon2)}
          </>
        );
      return renderIconList(isSelected, kpiOperationType.icon);
    }
    return null;
  };
  //#endregion

  //#region Renders
  const renderIconList = (isSelected: boolean, icon: string) => {
    return <Icon color={isSelected ? 'primary' : 'inherit'} size={30} icon={icon} />;
  };

  const renderListOperationTypes = () => {
    return (
      <GridContainerFullHeight>
        <List
          component="nav"
          className={className.scrollableList}
          /* subheader={<ListSubheader className={className.listSubheader}>{t('Kpi definitions')}</ListSubheader>} */
        >
          {listKpiDefinition?.map(item => (
            <ListItemButton
              key={item.logicalId}
              selected={kpiSelected?.getID() === item.logicalId}
              onClick={() => handleListItemClick(item)}
            >
              <ListItemIcon>{getIconFromKpiDefinition(item)}</ListItemIcon>
              <ListItemText className={className.listItemKpi} primary={<Typography text={item.name} />} />
            </ListItemButton>
          ))}
        </List>
      </GridContainerFullHeight>
    );
  };

  const renderListKPICards = () => {
    return (
      <Grid container size="auto" spacing={3} justifyContent="space-around" sx={{ maxHeight: '100px' }}>
        {listKPICards
          .filter(kpi => kpi.data.value !== undefined)
          .map((item, i) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
              <KPI.KPICard title={item.title} loading={item.loading} dataCard={item.data} withoutDifference />
            </Grid>
          ))}
      </Grid>
    );
  };

  const renderTableResults = () => {
    return (
      <Grid container size={'grow'} sx={{ height: '100%', overflowY: 'auto' }}>
        <KpiResults {...props} executionId={props.executionId} />
      </Grid>
    );
  };

  const renderInfoContent = () => {
    const parseICronParamToString = (cronParam: CronParamsDto): string => {
      const str =
        (cronParam.hour !== null ? '0' : cronParam.minute ?? '*') +
        ' ' +
        (cronParam.hour && cronParam.hour > 23 ? 23 : cronParam.hour ?? '*') +
        ' ' +
        (cronParam.dayOfMonth ?? '*') +
        ' ' +
        (cronParam.month ?? '*') +
        ' ' +
        (cronParam.dayOfWeek ?? '*');
      return str;
    };
    const cronsFormat = { locale: i18n.language, use24HourTimeFormat: true, verbose: true };
    const infoContent: any[] = [
      <b key="1">Report schedule: </b>,
      cronstrue.toString(kpiSelected.scheduleConfig.schedule, cronsFormat).toLocaleLowerCase(),
    ];

    infoContent.push([<br key="2" />, <b key="3">Windows data: </b>]);

    if (kpiSelected.scheduleConfig.dataPeriod.type === 'fixed') {
      infoContent.push([
        formatDate(kpiSelected.scheduleConfig.dataPeriod.params.start as string).toLocaleLowerCase(),
        ' to ',
        formatDate(kpiSelected.scheduleConfig.dataPeriod.params.end as string).toLocaleLowerCase(),
      ]);
    } else {
      infoContent.push([
        cronstrue
          .toString(parseICronParamToString(kpiSelected.scheduleConfig.dataPeriod.params.start as CronParamsDto), cronsFormat)
          .toLocaleLowerCase(),
        ' to ',
        cronstrue
          .toString(parseICronParamToString(kpiSelected.scheduleConfig.dataPeriod.params.end as CronParamsDto), cronsFormat)
          .toLocaleLowerCase(),
      ]);
    }

    return <IconButtonPopover infoContent={infoContent} />;
  };

  //#endregion

  useEffect(() => {
    //if the prop does not come from the prop, you have to load the kpi list,
    //in order to display it and to be able to select
    if (!props.kpiId) {
      //KPIList
      const fetchDataPromises = [getListKPIs(dispatch)];

      Promise.all(fetchDataPromises);
    }
  }, [props.kpiId]);

  useEffect(() => {
    //KPIsStatistics
    getDataStatistics();
  }, [kpiSelected?.getID()]);

  useEffect(() => {
    return () => {
      dispatch(KPIActions.updateKPIState({ key: 'kpiSelected', value: null }));
    };
  }, []);

  return (
    <GridContainerFullHeight id="kpi-metrics">
      {!props.kpiId && (
        <Grid container size={{ xs: 12, md: 2 }} className={className.gridLeft}>
          {renderListOperationTypes()}
        </Grid>
      )}
      <Grid
        container
        direction={'column'}
        size={{ md: !props.kpiId ? 10 : 12 }}
        spacing={2}
        px={1}
        pb={1}
        sx={{ height: '100%' }}
      >
        {kpiSelected?.getID() && (
          <>
            <Grid
              container
              size={'auto'}
              sx={{
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="h5">
                <b>{kpiSelected.getName()}</b>
                {renderInfoContent()}
              </Typography>
              <Typography variant="body2" text={[<b>Executed time:</b>, ' ', formatDate(timestampExecution)]} />
            </Grid>
            {renderListKPICards()}
            {renderTableResults()}
          </>
        )}
      </Grid>
    </GridContainerFullHeight>
  );
};

export default KpiMetrics;
